import { parseEmotion, parsePapers, targetPose } from "./catalog";
import {
  applyIdle,
  createIdleState,
  type IdleState,
} from "./idle";
import {
  createBubbleState,
  startSilence,
  startSpeak,
  startThink,
  tickBubble,
  type BubbleState,
} from "./bubble";
import { defaultPose, lerpNumber, lerpPose, type Pose } from "./pose";
import type { CaptionOptions, DwimSize } from "./theme";

export type EngineInput = {
  emotion: string;
  papers: string;
  size: DwimSize;
  reducedMotion: boolean;
  /** When set, only caption bubble motion is reduced; idle/preset animation still runs. */
  captionReducedMotion?: boolean;
};

function clonePose(pose: Pose): Pose {
  return {
    ...pose,
    eyeL: { ...pose.eyeL },
    eyeR: { ...pose.eyeR },
    mouth: { ...pose.mouth },
    tail1: { ...pose.tail1 },
    tail2: { ...pose.tail2 },
    papers: pose.papers.map((p) => ({ ...p })),
    bubble: { ...pose.bubble },
  };
}

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

function bubbleSnapshot(state: BubbleState): Pose["bubble"] {
  return {
    progress: state.progress,
    kind: state.kind,
    text: state.text,
    fill: state.fill,
    tone: state.tone,
  };
}

export class DwimEngine {
  private nowFn: () => number;
  private randomFn: () => number;
  private input: EngineInput = {
    emotion: "neutral",
    papers: "none",
    size: "hero",
    reducedMotion: false,
  };
  private fromPose: Pose = defaultPose();
  private currentBase: Pose = defaultPose();
  private currentPose: Pose = defaultPose();
  private tweenStart = 0;
  private lastNow: number | null = null;
  private orbitT = 0;
  private idleState: IdleState;
  private bubbleState: BubbleState = createBubbleState();
  private pendingResolve: (() => void) | null = null;
  private bubbleWasActive = false;

  constructor(options?: { now?: () => number; random?: () => number }) {
    this.nowFn = options?.now ?? Date.now;
    this.randomFn = options?.random ?? Math.random;
    this.idleState = createIdleState(this.nowFn(), this.randomFn);
  }

  setInput(input: EngineInput): void {
    const prevEmotion = parseEmotion(this.input.emotion);
    const prevPapers = parsePapers(this.input.papers);
    const nextEmotion = parseEmotion(input.emotion);
    const nextPapers = parsePapers(input.papers);
    this.input = input;
    if (prevEmotion !== nextEmotion || prevPapers !== nextPapers) {
      this.fromPose = clonePose(this.currentBase);
      this.tweenStart = this.clock();
    }
  }

  speak(text: string, options?: CaptionOptions): Promise<void> {
    return this.beginCaption("speak", text, options);
  }

  think(text: string, options?: CaptionOptions): Promise<void> {
    return this.beginCaption("think", text, options);
  }

  private captionReducedMotion(): boolean {
    return this.input.captionReducedMotion ?? this.input.reducedMotion;
  }

  silence(): void {
    this.resolvePending();
    this.bubbleState = startSilence(
      this.bubbleState,
      this.clock(),
      this.captionReducedMotion(),
    );
  }

  tick(nowMs?: number): Pose {
    const now = nowMs ?? this.nowFn();
    const dt = this.lastNow === null ? 0 : now - this.lastNow;
    if (this.lastNow === null) {
      this.tweenStart = now;
    }
    this.lastNow = now;

    const emotion = parseEmotion(this.input.emotion);
    const papers = parsePapers(this.input.papers);
    if (papers === "orbiting") {
      this.orbitT += 0.7 * (dt / 1000);
    }

    const target = targetPose(emotion, papers, this.orbitT);
    target.bubble = bubbleSnapshot(this.bubbleState);

    const duration = this.input.reducedMotion ? 150 : 520;
    const t = clamp01((now - this.tweenStart) / duration);
    const u = this.input.reducedMotion ? t : 1 - (1 - t) ** 3;
    const rotT = this.input.reducedMotion
      ? u
      : u < 0.85
        ? (u / 0.85) * 1.12
        : 1.12 - ((u - 0.85) / 0.15) * 0.12;

    const lerped = lerpPose(this.fromPose, target, u);
    lerped.rotation = lerpNumber(this.fromPose.rotation, target.rotation, rotT);

    this.bubbleState = tickBubble(this.bubbleState, now, this.captionReducedMotion());
    lerped.bubble = bubbleSnapshot(this.bubbleState);

    this.currentBase = clonePose(lerped);

    const idle = applyIdle(lerped, this.idleState, {
      nowMs: now,
      dtMs: dt,
      size: this.input.size,
      emotion,
      reducedMotion: this.input.reducedMotion,
      random: this.randomFn,
    });
    this.idleState = idle.state;
    this.currentPose = idle.pose;

    const active = this.bubbleState.phase !== "idle";
    if (this.bubbleWasActive && !active) {
      this.resolvePending();
    }
    this.bubbleWasActive = active;

    return this.currentPose;
  }

  pose(): Pose {
    return this.currentPose;
  }

  private beginCaption(
    kind: "speak" | "think",
    text: string,
    options?: CaptionOptions,
  ): Promise<void> {
    if (text.trim() === "") {
      return Promise.resolve();
    }
    this.resolvePending();
    const now = this.clock();
    this.bubbleState =
      kind === "speak"
        ? startSpeak(this.bubbleState, text, now, this.captionReducedMotion(), options)
        : startThink(this.bubbleState, text, now, this.captionReducedMotion(), options);
    if (this.bubbleState.phase === "idle") {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.pendingResolve = resolve;
    });
  }

  private clock(): number {
    return this.lastNow ?? this.nowFn();
  }

  private resolvePending(): void {
    if (this.pendingResolve) {
      this.pendingResolve();
      this.pendingResolve = null;
    }
  }
}
